// "Test me": the count is hidden for a while so the dancer checks they can hold it.
//
// The pattern alternates N phrases shown with N phrases hidden, cut at count 1, so the numeral always
// disappears and returns on a 1. What counts is the number of phrases played since Test me was switched on
// (`hiddenAfter`), not the position in the song, so the pattern runs the same inside a loop: the app counts the
// phrases as they are played (apps/web/src/engine/played.ts). `hiddenAt` is the same rule for a song played straight
// through: the phrases played are the phrases between `startAt` and t.

import { phrasesBetween } from './phrases.js';
import type { CountMap } from './grid.js';

export interface HidePattern {
  /** Phrases shown, then the same number hidden, and so on. */
  readonly phrases: 2 | 4 | 8;
  /** When the test began, in seconds. The phrase containing it is the first shown phrase. Default: the map's first count 1. Before it, nothing is hidden. */
  readonly startAt?: number;
}

/**
 * Is the count hidden, `played` phrases after Test me began (0 in the phrase it was switched on in)? Shown for `phrases`
 * phrases, then hidden for `phrases`, and so on. Negative `played` (before the start) is shown.
 */
export function hiddenAfter(played: number, phrases: 2 | 4 | 8): boolean {
  if (phrases !== 2 && phrases !== 4 && phrases !== 8) throw new RangeError('Test me hides for 2, 4 or 8 phrases');
  return played >= 0 && played % (2 * phrases) >= phrases;
}

/** Is the count hidden at time t, for a song played straight through from `startAt`? */
export function hiddenAt(map: CountMap, t: number, pattern: HidePattern): boolean {
  const { phrases, startAt = map.origin } = pattern;
  return hiddenAfter(phrasesBetween(map, startAt, t), phrases);
}
